import { useState, useEffect } from 'react';
import { X, Sparkles, Film, Tv, CheckCircle, XCircle, Eye, Loader } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { supabaseService } from '../../lib/supabase-service';
import { tmdbService } from '../../lib/tmdb-service';
import WatchPreferencesModal from './WatchPreferencesModal';

const RATING_OPTIONS = [
  { value: 'loved', label: 'Loved It!', emoji: '❤️', color: 'bg-pink-500' },
  { value: 'liked', label: 'Liked', emoji: '👍', color: 'bg-emerald-500' },
  { value: 'neutral', label: 'Neutral', emoji: '😐', color: 'bg-amber-500' },
  { value: 'hated', label: 'Disliked', emoji: '👎', color: 'bg-red-500' }
];

export default function AIRecommendationsModal({ userId, authUserId, onClose, onMovieAdded }) {
  const { addIdea } = useFamilyContext();
  const [loading, setLoading] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [showPreferences, setShowPreferences] = useState(false);
  const [processingRec, setProcessingRec] = useState(null);

  useEffect(() => {
    generateRecommendations();
  }, []);

  const generateRecommendations = async () => {
    setLoading(true);

    try {
      const response = await fetch('/api/anthropic/entertainment-recommendations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId })
      });

      const data = await response.json();

      if (data.success) {
        setRecommendations(data.recommendations);
        toast.success(`Got ${data.recommendations.length} personalized recommendations!`);
      } else {
        throw new Error(data.error || 'Failed to generate recommendations');
      }
    } catch (error) {
      console.error('Error generating recommendations:', error);
      toast.error(error.message || 'Failed to generate recommendations');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (rec) => {
    setProcessingRec(rec.id);

    try {
      let year = rec.year || rec.metadata?.year || null;
      let enrichedData = {
        description: rec.plot_summary || rec.recommendation_reason,
        is_tv: rec.type === 'tv',
        genre: rec.genres?.join(', '),
        director: rec.metadata?.director,
        rotten_tomatoes_critic: rec.metadata?.rtCritic,
        rotten_tomatoes_audience: rec.metadata?.rtAudience,
        link_metadata: {
          title: rec.title,
          type: rec.type,
          genres: rec.genres,
          year: year,
          cast: rec.metadata?.cast,
          ...rec.metadata,
          aiRecommendation: true,
          matchScore: rec.match_score
        }
      };

      // Always fetch from TMDB to get image and additional metadata
      const loadingToast = toast.loading('Fetching movie data from TMDB...');
      const tmdbResult = await tmdbService.findOrEnrichMovie(rec.title, year);
      toast.dismiss(loadingToast);

      if (tmdbResult.tmdbFound) {
        year = tmdbResult.releaseYear || year;
        // Merge TMDB data
        enrichedData = {
          ...enrichedData,
          description: enrichedData.description || tmdbResult.description,
          genre: enrichedData.genre || tmdbResult.genre,
          image: tmdbResult.image,
          director: enrichedData.director || tmdbResult.director,
          link_metadata: {
            ...enrichedData.link_metadata,
            tmdbId: tmdbResult.tmdbId,
            tmdbRating: tmdbResult.tmdbRating,
            imdbId: tmdbResult.imdbId
          }
        };
      } else {
        toast.error('Could not find movie on TMDB - skipping');
        setProcessingRec(null);
        return;
      }

      const result = await supabaseService.findOrCreateMovie(rec.title, year, {
        ...enrichedData,
        created_by: authUserId
      });

      // Update recommendation status
      await updateRecommendationStatus(rec.id, 'accepted');

      // Show appropriate toast based on whether it was an update or new entry
      if (result.isUpdate) {
        toast.success(`Updated "${rec.title}" with AI recommendation data!`);
      } else {
        toast.success(`Added "${rec.title}" to your watch list!`);
      }

      // Notify parent to refresh
      onMovieAdded?.();

      setRecommendations(prev => prev.filter(r => r.id !== rec.id));
    } catch (error) {
      console.error('Error accepting recommendation:', error);
      toast.error('Failed to add to recommendations');
    } finally {
      setProcessingRec(null);
    }
  };

  const handleReject = async (rec) => {
    setProcessingRec(rec.id);

    try {
      await updateRecommendationStatus(rec.id, 'rejected');
      toast.success('Noted - won\'t recommend similar content');
      setRecommendations(prev => prev.filter(r => r.id !== rec.id));
    } catch (error) {
      console.error('Error rejecting recommendation:', error);
      toast.error('Failed to update');
    } finally {
      setProcessingRec(null);
    }
  };

  const handleMarkSeen = async (rec, rating) => {
    setProcessingRec(rec.id);

    try {
      // 1. First, create/update movie in ideas table (like handleAccept)
      let year = rec.year || rec.metadata?.year || null;
      let enrichedData = {
        description: rec.plot_summary || rec.recommendation_reason,
        is_tv: rec.type === 'tv',
        genre: rec.genres?.join(', '),
        director: rec.metadata?.director,
        rotten_tomatoes_critic: rec.metadata?.rtCritic,
        rotten_tomatoes_audience: rec.metadata?.rtAudience,
        link_metadata: {
          title: rec.title,
          type: rec.type,
          genres: rec.genres,
          year: year,
          cast: rec.metadata?.cast,
          ...rec.metadata,
          aiRecommendation: true,
          matchScore: rec.match_score
        }
      };

      // Always fetch from TMDB to get image and additional metadata
      const loadingToast = toast.loading('Fetching movie data from TMDB...');
      const tmdbResult = await tmdbService.findOrEnrichMovie(rec.title, year);
      toast.dismiss(loadingToast);

      if (tmdbResult.tmdbFound) {
        year = tmdbResult.releaseYear || year;
        // Merge TMDB data
        enrichedData = {
          ...enrichedData,
          description: enrichedData.description || tmdbResult.description,
          genre: enrichedData.genre || tmdbResult.genre,
          image: tmdbResult.image,
          director: enrichedData.director || tmdbResult.director,
          link_metadata: {
            ...enrichedData.link_metadata,
            tmdbId: tmdbResult.tmdbId,
            tmdbRating: tmdbResult.tmdbRating,
            imdbId: tmdbResult.imdbId
          }
        };
      } else {
        toast.error('Could not find movie on TMDB - skipping');
        setProcessingRec(null);
        return;
      }

      const result = await supabaseService.findOrCreateMovie(rec.title, year, {
        ...enrichedData,
        created_by: authUserId
      });

      // 2. Log watch history with proper entertainment_id link
      const watchResponse = await fetch('/api/watch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'mark-as-seen',
          userId,
          entertainmentId: result.id,  // ✓ Proper link to ideas table
          rating: rating
        })
      });

      const watchData = await watchResponse.json();
      if (!watchData.success) throw new Error('Failed to log watch history');

      // Update recommendation status
      await updateRecommendationStatus(rec.id, 'seen', rating);

      const ratingOption = RATING_OPTIONS.find(r => r.value === rating);
      toast.success(
        `Marked "${rec.title}" as seen and rated "${ratingOption?.label}" ${ratingOption?.emoji}`,
        { icon: ratingOption?.emoji, duration: 3000 }
      );

      // Notify parent to refresh
      onMovieAdded?.();

      setRecommendations(prev => prev.filter(r => r.id !== rec.id));
    } catch (error) {
      console.error('Error marking as seen:', error);
      toast.error('Failed to update');
    } finally {
      setProcessingRec(null);
    }
  };

  const updateRecommendationStatus = async (recId, status, rating = null) => {
    // This would be a new API endpoint to update entertainment_recommendations table
    // For now, we'll just use the existing structure
    console.log('Update recommendation', recId, status, rating);
  };

  if (showPreferences) {
    return (
      <WatchPreferencesModal
        userId={userId}
        onClose={() => setShowPreferences(false)}
        onSave={() => {
          setShowPreferences(false);
          generateRecommendations();
        }}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-4xl w-full my-8 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-indigo-600 via-violet-600 to-pink-600 text-white p-6 rounded-t-2xl z-10">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Sparkles className="w-8 h-8" />
                <h2 className="text-2xl font-bold">AI Watch Recommendations</h2>
              </div>
              <p className="text-white text-opacity-90 text-sm">
                Personalized suggestions based on your watch history and preferences
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="mt-4 flex gap-2">
            <button
              onClick={() => setShowPreferences(true)}
              className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg text-sm font-medium transition-colors"
            >
              ⚙️ Update Preferences
            </button>
            <button
              onClick={generateRecommendations}
              disabled={loading}
              className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            >
              {loading ? '⏳ Generating...' : '🔄 Refresh Recommendations'}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader className="w-12 h-12 text-indigo-600 animate-spin mb-4" />
              <p className="text-stone-600">Analyzing your preferences...</p>
            </div>
          ) : recommendations.length === 0 ? (
            <div className="text-center py-12">
              <Sparkles className="w-16 h-16 text-stone-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-stone-900 mb-2">No Recommendations Yet</h3>
              <p className="text-stone-600 mb-4">
                Watch and rate some movies/shows first, then we'll generate personalized recommendations!
              </p>
              <button
                onClick={() => setShowPreferences(true)}
                className="px-6 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition-colors"
              >
                Set Your Preferences
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {recommendations.map((rec) => (
                <RecommendationCard
                  key={rec.id}
                  recommendation={rec}
                  onAccept={() => handleAccept(rec)}
                  onReject={() => handleReject(rec)}
                  onMarkSeen={(rating) => handleMarkSeen(rec, rating)}
                  isProcessing={processingRec === rec.id}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RecommendationCard({ recommendation, onAccept, onReject, onMarkSeen, isProcessing }) {
  const [showRatingOptions, setShowRatingOptions] = useState(false);

  const { title, type, genres, recommendation_reason, match_score, metadata, year, plot_summary } = recommendation;

  return (
    <div className="bg-gradient-to-br from-white to-stone-50 border border-stone-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            {type === 'tv' ? (
              <Tv className="w-5 h-5 text-violet-600" />
            ) : (
              <Film className="w-5 h-5 text-indigo-600" />
            )}
            <h3 className="text-xl font-bold text-stone-900">{title}</h3>
            {year && (
              <span className="text-sm font-semibold text-stone-600">({year})</span>
            )}
          </div>

          {genres && genres.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {genres.map((genre, i) => (
                <span
                  key={i}
                  className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded"
                >
                  {genre}
                </span>
              ))}
            </div>
          )}

          {/* Cast - show prominently near title */}
          {metadata?.cast && metadata.cast.length > 0 && (
            <div className="text-sm text-stone-600 mb-2">
              <span className="font-semibold">Starring:</span> {metadata.cast.slice(0, 3).join(', ')}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 ml-4">
          <div className="text-right">
            <div className="text-2xl font-bold text-indigo-600">{match_score}%</div>
            <div className="text-xs text-stone-500">Match</div>
          </div>
        </div>
      </div>

      {/* Plot Summary */}
      {plot_summary && (
        <div className="mb-3 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
          <p className="text-sm text-stone-700 italic leading-relaxed">{plot_summary}</p>
        </div>
      )}

      {/* Why it's recommended */}
      <div className="mb-4">
        <p className="text-sm text-stone-600 leading-relaxed">
          <span className="font-semibold text-stone-700">Why for you:</span> {recommendation_reason}
        </p>
      </div>

      {metadata && (
        <div className="grid grid-cols-2 gap-2 mb-4 text-sm">
          {metadata.director && (
            <div>
              <span className="font-semibold">Director:</span> {metadata.director}
            </div>
          )}
          {metadata.estimatedRuntime && (
            <div>
              <span className="font-semibold">Runtime:</span> {metadata.estimatedRuntime}
            </div>
          )}
          {metadata.rtCritic && (
            <div>
              <span className="font-semibold">RT Critics:</span> {metadata.rtCritic}%
            </div>
          )}
          {metadata.rtAudience && (
            <div>
              <span className="font-semibold">RT Audience:</span> {metadata.rtAudience}%
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={onAccept}
          disabled={isProcessing}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold rounded-lg transition-all disabled:opacity-50"
        >
          <CheckCircle className="w-5 h-5" />
          Add to Library
        </button>

        <button
          onClick={() => setShowRatingOptions(!showRatingOptions)}
          className="px-4 py-3 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 font-semibold rounded-lg transition-colors"
        >
          <Eye className="w-5 h-5" />
        </button>

        <button
          onClick={onReject}
          disabled={isProcessing}
          className="px-4 py-3 bg-stone-100 hover:bg-stone-200 text-stone-700 font-semibold rounded-lg transition-colors disabled:opacity-50"
        >
          <XCircle className="w-5 h-5" />
        </button>
      </div>

      {/* Quick Rating for "Already Seen" */}
      {showRatingOptions && (
        <div className="mt-3 p-4 bg-stone-50 border border-stone-200 rounded-lg">
          <div className="text-sm font-semibold text-stone-700 mb-2">Already seen it? Rate it:</div>
          <div className="grid grid-cols-4 gap-2">
            {RATING_OPTIONS.map(({ value, label, emoji, color }) => (
              <button
                key={value}
                onClick={() => onMarkSeen(value)}
                disabled={isProcessing}
                className={`flex flex-col items-center gap-1 p-2 ${color} hover:opacity-90 text-white font-medium rounded-lg transition-all disabled:opacity-50`}
              >
                <span className="text-xl">{emoji}</span>
                <span className="text-xs">{label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {isProcessing && (
        <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center rounded-xl">
          <Loader className="w-8 h-8 text-indigo-600 animate-spin" />
        </div>
      )}
    </div>
  );
}
